<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import chevron from "@core/assets/icons/chevron.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getClientsTypicalProblems } from "../data/getClientsTypicalProblems.js";
	import { getFaqsClients } from "../data/getFaqsClients.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import type { ReferenceProps } from "./ReferenceProps.js";

	const { partners }: ReferenceProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const anchorReferences = "reference";
	let referencesExpanded = $state(false);
</script>

<Layout>
	<Section
		paddingFollowNext
		background={Background.LightGradient}
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		hero
	>
		{#snippet headline()}
			<Formatted text="cg[[Spoléhají na nás]] malé firmy i světoznámé značky" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="co[[wm[[Komu pomáháme zrychlit růst podnikání?]]]] nl[[]] Získali jsme si důvěru u podniků napříč Českem a obory"
				/>
			</p>
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Basic)} />
	<div class="flex flex-col mt-8 lg:mt-12 gap-3">
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center"
			href="#{anchorReferences}"
			>Zobrazit reference
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</div>

	<Section id={anchorReferences} gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Mezi našimi cg[[klienty]] je cukrárna, Volvo i advokátní kancelář" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents swapReversed limit={6} />
			<button
				type="button"
				onclick={() => {
					referencesExpanded = !referencesExpanded;
				}}
				class="flex items-center justify-center gap-2 mt-8 mx-auto text-fstgreen font-medium hover:opacity-70 transition-opacity select-none cursor-pointer text-md"
			>
				<span class="text-fstgreen">{referencesExpanded ? "Skrýt reference" : "Zobrazit všechny reference"}</span>
				<Icon
					icon={chevron}
					class={["w-6 h-6 mt-0.5 transition-transform duration-300", referencesExpanded ? "rotate-90" : "-rotate-90"]}
				/>
			</button>
			<div
				class="grid overflow-hidden"
				style:grid-template-rows={referencesExpanded ? "1fr" : "0fr"}
				style:transition="grid-template-rows 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
			>
				<div class="overflow-hidden min-h-0">
					<div class="mt-2">
						<ReferenceContents swapReversed startIndex={6} />
					</div>
				</div>
			</div>
		</div>
	</Section>

	<Section background={Background.LightGradient} noPadding="both" gridHeadline="container-6 lg:text-center">
		{#snippet headline()}
			<Formatted text="S čím cg[[za námi]] klienti často chodí" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getClientsTypicalProblems(tenant) as { title, desc, iconName, items }, index (index)}
				<InfoCard {title} {desc} {iconName} {items} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Chci fastest služby" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Odpovědi na cg[[časté otázky]]" />
		{/snippet}
		<FaqItems faqs={getFaqsClients(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
